import {
    Component,
    createRef,
    CSSProperties,
    PointerEvent as ReactPointerEvent,
    Ref,
} from 'react';
import './greetings.css';
import {
    FaCloud,
    FaCode,
    FaCogs,
    FaLaptopCode,
    FaMapMarkerAlt,
    FaPause,
    FaPlay,
    FaRobot,
    FaSyncAlt,
} from 'react-icons/fa';
import {
    SiDotnet,
    SiGithubactions,
    SiJavascript,
    SiKubernetes,
    SiPython,
    SiReact,
    SiTypescript,
} from 'react-icons/si';
import { TbBuildingFactory2 } from 'react-icons/tb';
import { VscAzure, VscCopilot, VscVscode } from 'react-icons/vsc';
import { getCareerDurationLabel } from '../../utils/career-duration';
import { CountryFlag } from '../country-flag/country-flag';

const technologyStack = [
    { label: 'Microsoft Azure', Icon: VscAzure },
    { label: 'Microsoft Foundry', Icon: TbBuildingFactory2 },
    { label: 'GitHub Copilot', Icon: VscCopilot },
    { label: 'GitHub Actions', Icon: SiGithubactions },
    { label: 'Azure DevOps', Icon: VscAzure },
    { label: 'Microsoft Copilot Studio', Icon: VscCopilot },
    { label: 'Azure OpenAI Service', Icon: VscAzure },
    { label: 'Azure AI Search', Icon: VscAzure },
    { label: 'Azure Kubernetes Service', Icon: SiKubernetes },
    { label: 'Azure API Management', Icon: VscAzure },
    { label: 'Azure Logic Apps', Icon: VscAzure },
    { label: 'Microsoft Entra ID', Icon: VscAzure },
    { label: 'Microsoft Agent Framework', Icon: FaRobot },
    { label: 'Semantic Kernel', Icon: FaCode },
    { label: 'Visual Studio Code', Icon: VscVscode },
    { label: 'Model Context Protocol', Icon: FaCogs },
    { label: '.NET', Icon: SiDotnet },
    { label: 'ASP.NET Core', Icon: SiDotnet },
    { label: 'React', Icon: SiReact },
    { label: 'TypeScript', Icon: SiTypescript },
    { label: 'JavaScript', Icon: SiJavascript },
    { label: 'Python', Icon: SiPython },
    { label: 'C#', Icon: FaCode },
];

const TechnologyStackList = ({
    duplicate = false,
    listRef,
}: {
    duplicate?: boolean;
    listRef?: Ref<HTMLUListElement>;
}) => (
    <ul
        ref={listRef}
        className="greeting-stack-list"
        aria-label={duplicate ? undefined : 'Technologies, products, and solutions'}
        aria-hidden={duplicate || undefined}
    >
        {technologyStack.map(({ label, Icon }) => (
            <li key={label}><Icon aria-hidden="true" /> {label}</li>
        ))}
    </ul>
);

interface GreetingState {
    isStackPaused: boolean;
    isStackDragging: boolean;
    stackDragOffset: number;
}

export class Greeting extends Component<{}, GreetingState> {
    state = {
        isStackPaused: false,
        isStackDragging: false,
        stackDragOffset: 0,
    };

    private stackListRef = createRef<HTMLUListElement>();
    private dragStartX = 0;
    private dragStartOffset = 0;

    private normalizeStackOffset = (offset: number) => {
        const stackWidth = this.stackListRef.current?.getBoundingClientRect().width ?? 0;

        if (stackWidth === 0) {
            return offset;
        }

        return ((offset + stackWidth / 2) % stackWidth + stackWidth) % stackWidth - stackWidth / 2;
    };

    private handleStackPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'mouse' && event.button !== 0) {
            return;
        }

        this.dragStartX = event.clientX;
        this.dragStartOffset = this.state.stackDragOffset;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        this.setState({ isStackDragging: true });
    };

    private handleStackPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (!this.state.isStackDragging) {
            return;
        }

        const stackDragOffset = this.normalizeStackOffset(
            this.dragStartOffset + event.clientX - this.dragStartX
        );
        this.setState({ stackDragOffset });
    };

    private handleStackPointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (!this.state.isStackDragging) {
            return;
        }

        event.currentTarget.releasePointerCapture?.(event.pointerId);
        this.setState({ isStackDragging: false });
    };

    render() {
        const { isStackPaused, isStackDragging, stackDragOffset } = this.state;
        const dragStyle = {
            '--stack-drag-offset': `${stackDragOffset}px`,
        } as CSSProperties;

        return (
            <section id="profile" className="greeting" aria-labelledby="greeting-heading">
                <img
                    className="greeting-photo"
                    alt=""
                    src="/portrait-240.webp"
                    srcSet="/portrait-240.webp 240w, /portrait-460.webp 460w"
                    sizes="(max-width: 480px) 144px, (max-width: 900px) 168px, (max-height: 700px) 200px, 240px"
                    height="240"
                    width="240"
                    loading="eager"
                    decoding="async"
                />
                <h1 className="greeting-name" id="greeting-heading">Ahmed Magdy</h1>
                <p className="greeting-brand">Copiloteer</p>
                <p className="greeting-welcome">
                    <span lang="ar" dir="rtl">مَرْحَبًا</span> - Greetings!{' '}
                    <img className="greeting-wave" src="/waving-hand.svg" alt="" height="48" width="48" />
                </p>
                <p className="greeting-title">
                    Senior Cloud &amp; AI Apps Solution Engineer at{' '}
                    <a href="https://www.microsoft.com/" target="_blank" rel="noreferrer">
                        <span className="microsoft-mark" aria-hidden="true">
                            <i /><i /><i /><i />
                        </span>
                        <span>Microsoft</span>
                    </a>
                </p>
                <p className="greeting-location">
                    <span><CountryFlag country="Egypt" className="greeting-location-flag" /> From Egypt</span>{' '}
                    <span className="greeting-location-separator" aria-hidden="true">·</span>{' '}
                    <span>
                        <FaMapMarkerAlt aria-hidden="true" />
                        <CountryFlag country="Saudi Arabia" className="greeting-location-flag" />
                        Based in Saudi Arabia
                    </span>
                </p>
                <div className="greeting-body">
                    <p>
                        With {getCareerDurationLabel()} spanning software engineering, cloud solution
                        architecture, technical presales, and AI, I help organizations turn ambitious ideas
                        into secure, scalable, production-ready applications.
                    </p>
                    <ul className="greeting-expertise" aria-label="Areas of expertise">
                        <li><FaRobot aria-hidden="true" /> Agentic AI</li>
                        <li><FaCloud aria-hidden="true" /> Cloud Architecture</li>
                        <li><FaSyncAlt aria-hidden="true" /> App Modernization</li>
                        <li><FaCogs aria-hidden="true" /> DevOps &amp; Automation</li>
                        <li><FaLaptopCode aria-hidden="true" /> Developer Productivity</li>
                        <li><FaCode aria-hidden="true" /> Software Engineering</li>
                    </ul>
                    <p>
                        At Microsoft, I partner with technical leaders and engineering teams to modernize
                        applications, design cloud-native architectures, and build agentic AI solutions.
                        I also help teams improve software delivery and developer productivity through modern
                        engineering practices, DevOps, automation, and AI-assisted development.
                    </p>
                    <div className="greeting-stack">
                        <div className="greeting-stack-heading">
                            <span>STACK://PRODUCTS/TECHNOLOGIES</span>
                            <button
                                type="button"
                                onClick={() => this.setState({ isStackPaused: !isStackPaused })}
                                aria-label={`${isStackPaused ? 'Resume' : 'Pause'} technology stack`}
                                title={`${isStackPaused ? 'Resume' : 'Pause'} technology stack`}
                            >
                                {isStackPaused
                                    ? <FaPlay aria-hidden="true" />
                                    : <FaPause aria-hidden="true" />}
                            </button>
                        </div>
                        <div
                            className={`greeting-stack-viewport${isStackDragging ? ' is-dragging' : ''}`}
                            role="group"
                            aria-label="Draggable technology stack"
                            title="Drag left or right to explore the technology stack"
                            onPointerDown={this.handleStackPointerDown}
                            onPointerMove={this.handleStackPointerMove}
                            onPointerUp={this.handleStackPointerEnd}
                            onPointerCancel={this.handleStackPointerEnd}
                        >
                            <div className="greeting-stack-drag" style={dragStyle}>
                                <div className={`greeting-stack-track${isStackPaused ? ' is-paused' : ''}`}>
                                    <TechnologyStackList listRef={this.stackListRef} />
                                    <TechnologyStackList duplicate />
                                    <TechnologyStackList duplicate />
                                    <TechnologyStackList duplicate />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }
}